import { motion } from 'framer-motion';
import { BookOpen, Check, Search } from 'lucide-react';
import React, { useRef } from 'react';
import type { LearningSession } from '../types';
import { getBookTheme } from '../utils/bookTheme';

interface LearningNodeProps {
  session: LearningSession;
  onClick: (session: LearningSession) => void;
  isModalOpen?: boolean;
}

const LearningNode: React.FC<LearningNodeProps> = ({ session, onClick, isModalOpen }) => {
  const { isCompleted, isLocked, isActive, progress, heatLevel, iconType, kind } = session;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const isAnalysisNode = kind === 'book-analysis' || iconType === 'analysis';
  const theme = getBookTheme(session.bookId);

  const handleNodeClick = () => {
    audioRef.current?.play();
    if (navigator.vibrate) navigator.vibrate(20);
    onClick(session);
  };

  const outerSize = 96;
  const strokeWidth = 6;
  const ringRadius = (outerSize / 2) - (strokeWidth / 2);
  const circumference = 2 * Math.PI * ringRadius;
  const innerSize = outerSize - (strokeWidth * 2) - 10;
  const currentProgress = isCompleted ? 100 : (isActive ? (progress || 0) : 0);
  const strokeDashoffset = circumference - (currentProgress / 100) * circumference;

  return (
    <div className="flex flex-col items-center z-10 relative">
      {isActive && !isModalOpen && !isAnalysisNode && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: [0, -6, 0] }}
          transition={{
            opacity: { duration: 0.3 },
            y: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' }
          }}
          className="absolute -top-14 z-20"
        >
          <div className="bg-white font-bold px-4 py-1.5 rounded-xl shadow-bubble relative border-2 border-gray-100 whitespace-nowrap">
            <span className="font-persian text-sm" style={{ color: theme.primary }}>شروع کن</span>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-b-2 border-r-2 border-gray-100 rotate-45" />
          </div>
        </motion.div>
      )}

      <motion.div
        ref={nodeRef}
        whileTap={!isLocked ? { scale: 0.9 } : {}}
        className="relative flex items-center justify-center"
        style={{ width: outerSize, height: outerSize }}
      >
        {!isAnalysisNode && (
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox={`0 0 ${outerSize} ${outerSize}`}
            style={{ transform: 'rotate(-90deg)' }}
          >
            <circle
              cx={outerSize / 2}
              cy={outerSize / 2}
              r={ringRadius}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={strokeWidth}
            />
            {!isLocked && (
              <circle
                cx={outerSize / 2}
                cy={outerSize / 2}
                r={ringRadius}
                fill="none"
                stroke={theme.primary}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-500 ease-out"
              />
            )}
          </svg>
        )}

        {!isAnalysisNode && heatLevel && heatLevel >= 7 && (() => {
          const maxScale = 1.4 + (heatLevel - 7) * 0.1;
          const loopDuration = 3 - (heatLevel - 7) * 0.5;

          return (
            <motion.div
              className="absolute rounded-full pointer-events-none"
              style={{
                width: innerSize,
                height: innerSize,
                backgroundColor: theme.primary
              }}
              animate={{ scale: [1, maxScale, 1], opacity: [0.1, 0.4, 0.1] }}
              transition={{ duration: loopDuration, repeat: Infinity, ease: 'easeInOut' }}
            />
          );
        })()}

        <div
          onClick={handleNodeClick}
          className={`
            absolute rounded-full flex items-center justify-center
            transition-all duration-200
            ${isLocked ? 'cursor-pointer' : 'cursor-pointer hover:brightness-110'}
          `}
          style={{
            width: innerSize,
            height: innerSize,
            background: isLocked
              ? '#ffffff'
              : `linear-gradient(145deg, ${theme.primary}, ${theme.primaryDark})`,
            boxShadow: isLocked
              ? 'inset 0 0 0 2px #e5e7eb'
              : `0 4px 0 0 ${theme.primaryDark}, 0 8px 10px rgba(0,0,0,0.2)`,
            transform: isLocked ? 'none' : 'translateY(-2px)'
          }}
        >
          {isCompleted || iconType === 'check' ? (
            <Check className="w-6 h-6 text-white drop-shadow-md" strokeWidth={3} />
          ) : isAnalysisNode ? (
            <Search className={`w-6 h-6 drop-shadow-md ${isLocked ? 'text-gray-400' : 'text-white'}`} />
          ) : (
            <BookOpen className={`w-6 h-6 drop-shadow-md ${isLocked ? 'text-gray-400' : 'text-white'}`} />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default LearningNode;
